import { z } from "zod";

/**
 * Shared client-side validation for publishing surveys.
 * Stricter than draft validation: ensures questions are answerable,
 * required metadata is present, and lengths are bounded.
 */

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 2000;
const SLUG_MAX = 80;
const PHONE_MAX = 30;
const RELEASED_BY_MAX = 200;
const QUESTION_TEXT_MAX = 1000;
const OPTION_MAX = 200;
const MAX_QUESTIONS = 200;

const slugRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export interface PublishQuestion {
  question_text: string;
  question_type: string;
  options: any;
  is_required?: boolean;
}

export interface PublishSurveyInput {
  title: string;
  description?: string | null;
  is_public: boolean;
  public_slug?: string | null;
  target_roles: string[];
  inquiry_phone?: string | null;
  released_by?: string | null;
  questions: PublishQuestion[];
}

const surveyMetaSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { message: "Title is required" })
    .max(TITLE_MAX, { message: `Title must be ${TITLE_MAX} characters or fewer` }),
  description: z
    .string()
    .trim()
    .max(DESCRIPTION_MAX, { message: `Description must be ${DESCRIPTION_MAX} characters or fewer` })
    .optional()
    .nullable(),
  is_public: z.boolean(),
  public_slug: z
    .string()
    .trim()
    .max(SLUG_MAX, { message: `Public link must be ${SLUG_MAX} characters or fewer` })
    .optional()
    .nullable(),
  inquiry_phone: z
    .string()
    .trim()
    .max(PHONE_MAX, { message: `Inquiry phone must be ${PHONE_MAX} characters or fewer` })
    .optional()
    .nullable(),
  released_by: z
    .string()
    .trim()
    .max(RELEASED_BY_MAX, { message: "'Released by' value is too long" })
    .optional()
    .nullable(),
});

const CHOICE_TYPES = ["single_choice", "multiple_choice", "dropdown"];
const GRID_TYPES = ["checkbox_grid", "mcq_grid"];

/**
 * Validate that a survey is ready to be published.
 * Returns { ok: true } on success, or { ok: false, errors: string[] }.
 */
export function validateSurveyForPublish(input: PublishSurveyInput): { ok: true } | { ok: false; errors: string[] } {
  const errors: string[] = [];

  // 1. Top-level metadata
  const metaResult = surveyMetaSchema.safeParse({
    title: input.title,
    description: input.description ?? null,
    is_public: input.is_public,
    public_slug: input.public_slug ?? null,
    inquiry_phone: input.inquiry_phone ?? null,
    released_by: input.released_by ?? null,
  });
  if (!metaResult.success) {
    metaResult.error.issues.forEach(i => errors.push(i.message));
  }

  // 2. Public surveys must have a URL slug
  if (input.is_public) {
    const slug = (input.public_slug || "").trim();
    if (!slug) {
      errors.push("Public surveys require a public link (slug) before publishing");
    } else if (!slugRegex.test(slug)) {
      errors.push("Public link may only contain lowercase letters, numbers, and hyphens");
    }
  }

  // 3. Internal surveys need at least one targeted role
  if (!input.is_public && (!input.target_roles || input.target_roles.length === 0)) {
    errors.push("Select at least one target role before publishing an internal survey");
  }

  // 4. Question count
  if (!input.questions || input.questions.length === 0) {
    errors.push("Add at least one question before publishing");
  } else if (input.questions.length > MAX_QUESTIONS) {
    errors.push(`A survey can contain at most ${MAX_QUESTIONS} questions`);
  }

  // 5. Per-question checks
  (input.questions || []).forEach((q, idx) => {
    const label = `Question ${idx + 1}`;
    const text = (q.question_text || "").trim();
    if (!text) {
      errors.push(`${label}: question text is required`);
    } else if (text.length > QUESTION_TEXT_MAX) {
      errors.push(`${label}: question text must be ${QUESTION_TEXT_MAX} characters or fewer`);
    }

    if (CHOICE_TYPES.includes(q.question_type)) {
      // Either a database-sourced option set or a manual list with ≥2 non-empty values
      const opts = q.options;
      const isDbSource = opts && typeof opts === "object" && !Array.isArray(opts) && opts.source;
      if (isDbSource) {
        const values = Array.isArray(opts.values) ? opts.values : [];
        if (values.length === 0) {
          errors.push(`${label}: linked data source returned no options — load options before publishing`);
        }
      } else {
        const arr = (Array.isArray(opts) ? opts : []) as string[];
        const cleaned = arr.map(o => (o || "").trim()).filter(Boolean);
        if (cleaned.length < 2) {
          errors.push(`${label}: choice questions need at least 2 non-empty options`);
        }
        const tooLong = cleaned.find(o => o.length > OPTION_MAX);
        if (tooLong) {
          errors.push(`${label}: each option must be ${OPTION_MAX} characters or fewer`);
        }
        const unique = new Set(cleaned.map(o => o.toLowerCase()));
        if (unique.size !== cleaned.length) {
          errors.push(`${label}: option values must be unique`);
        }
      }
    } else if (GRID_TYPES.includes(q.question_type)) {
      const grid = q.options && typeof q.options === "object" && !Array.isArray(q.options) ? q.options : { rows: [], columns: [] };
      const rows = (Array.isArray(grid.rows) ? grid.rows : []).map((r: string) => (r || "").trim()).filter(Boolean);
      const cols = (Array.isArray(grid.columns) ? grid.columns : []).map((c: string) => (c || "").trim()).filter(Boolean);
      if (rows.length === 0) errors.push(`${label}: grid needs at least one row`);
      if (cols.length === 0) errors.push(`${label}: grid needs at least one column`);
    } else if (q.question_type === "linear_scale") {
      const arr = (Array.isArray(q.options) ? q.options : []) as string[];
      const min = Number(arr[0]);
      const max = Number(arr[1]);
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        errors.push(`${label}: linear scale needs numeric min and max`);
      } else if (min >= max) {
        errors.push(`${label}: linear scale min must be less than max`);
      }
    }
  });

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true };
}

/** Format a list of validation errors into a single user-facing string. */
export function formatPublishErrors(errors: string[], max = 4): string {
  if (errors.length === 0) return "";
  const shown = errors.slice(0, max);
  const remaining = errors.length - shown.length;
  const suffix = remaining > 0 ? `\n…and ${remaining} more issue${remaining === 1 ? "" : "s"}` : "";
  return `Cannot publish:\n• ${shown.join("\n• ")}${suffix}`;
}
